import { CreditCard, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { CreditAccount } from "@/hooks/useCredit";

interface CreditAccountCardProps {
  credit: CreditAccount | null;
  isLocked: boolean;
  availableCredit: number;
  utilization: number;
  onMakePayment: () => void;
  onUnlock: () => void;
}

const CreditAccountCard = ({
  credit,
  isLocked,
  availableCredit,
  utilization,
  onMakePayment,
  onUnlock,
}: CreditAccountCardProps) => {
  const utilizationColor =
    utilization > 75 ? "text-destructive" :
    utilization > 50 ? "text-amber-500" :
    "text-emerald-500";

  const utilizationBarColor =
    utilization > 75 ? "[&>div]:bg-destructive" :
    utilization > 50 ? "[&>div]:bg-amber-500" :
    "[&>div]:bg-emerald-500";

  if (isLocked) {
    return (
      <div className="col-span-2 relative rounded-2xl border border-border/50 bg-muted/30 p-5 opacity-70">
        <div className="absolute top-3 right-3">
          <Lock className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-muted">
            <CreditCard className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-sm text-foreground">Credit Account</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {credit?.unlock_condition || "Complete Credit Basics lessons"}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <p className="font-heading font-bold text-lg text-muted-foreground">Locked</p>
        </div>
        <div className="mt-3">
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={onUnlock}>
            Unlock Credit Account
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-2 rounded-2xl border bg-gradient-to-br from-secondary/20 to-primary/5 border-secondary/30 p-5 transition-all duration-300 hover:shadow-glow">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-card/80 shadow-sm">
          <CreditCard className="w-5 h-5 text-secondary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-sm text-foreground">Credit Account</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Revolving credit — borrow and repay</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <p className="text-xs text-muted-foreground">Balance</p>
          <p className="font-heading font-bold text-xl text-foreground">
            ${credit?.current_balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Credit Limit</p>
          <p className="font-heading font-semibold text-sm text-foreground">
            ${credit?.credit_limit.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Available</p>
          <p className="font-heading font-semibold text-sm text-foreground">
            ${availableCredit.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Min. Payment</p>
          <p className="font-heading font-semibold text-sm text-foreground">
            ${credit?.minimum_payment.toLocaleString() || "0"}
          </p>
        </div>
      </div>

      {/* Utilization bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Utilization</span>
          <span className={`text-xs font-semibold ${utilizationColor}`}>{utilization}%</span>
        </div>
        <Progress value={utilization} className={`h-2 ${utilizationBarColor}`} />
        {utilization > 50 && (
          <div className="flex items-center gap-1 mt-1.5">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            <span className="text-[10px] text-amber-600">
              Using too much of your limit can hurt your score.
            </span>
          </div>
        )}
      </div>

      {credit && credit.current_balance > 0 && (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onMakePayment}
            className="w-full text-xs gap-1.5 border-border/60 hover:bg-card/60"
          >
            <CreditCard className="w-3 h-3" />
            Make Payment
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreditAccountCard;
